export const SYSTEM_NOTIFICATIONS_RTDB_PATH = "global/systemNotifications";
export const SYSTEM_NOTIFICATION_MESSAGE_MAX = 500;

export type SystemNotificationType = "warning" | "information" | "success";

export interface SystemNotificationRecord {
  type: SystemNotificationType;
  message: string;
  createdAt: number;
  expiresAt: number;
  createdBy: string;
  sendPush?: boolean;
  pushTitle?: string;
  pushBody?: string;
  pushSentAt?: number;
}

export interface SystemNotificationEntry {
  id: string;
  record: SystemNotificationRecord;
}

const TYPE_ORDER: Record<SystemNotificationType, number> = {
  warning: 0,
  information: 1,
  success: 2,
};

export function formatSystemNotificationTimestamp(ms: number): string {
  if (!Number.isFinite(ms)) {
    return "";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ms));
}

export function alertColorForNotificationType(
  type: SystemNotificationType
): "warning" | "info" | "success" {
  if (type === "warning") {
    return "warning";
  }
  if (type === "success") {
    return "success";
  }
  return "info";
}

export function parseActiveSystemNotifications(
  data: Record<string, SystemNotificationRecord> | null | undefined,
  nowMs: number = Date.now()
): SystemNotificationEntry[] {
  if (!data || typeof data !== "object") {
    return [];
  }
  return Object.entries(data)
    .filter(([, record]) => record && Number(record.expiresAt) > nowMs)
    .map(([id, record]) => ({ id, record }))
    .sort((a, b) => {
      const createdDiff = b.record.createdAt - a.record.createdAt;
      if (createdDiff !== 0) {
        return createdDiff;
      }
      return TYPE_ORDER[a.record.type] - TYPE_ORDER[b.record.type];
    });
}

export function parseAllSystemNotifications(
  data: Record<string, SystemNotificationRecord> | null | undefined
): SystemNotificationEntry[] {
  if (!data || typeof data !== "object") {
    return [];
  }
  return Object.entries(data)
    .filter(([, record]) => record && typeof record === "object")
    .map(([id, record]) => ({ id, record }))
    .sort((a, b) => b.record.createdAt - a.record.createdAt);
}

export function toDatetimeLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function minDatetimeLocalFromNow(): string {
  return toDatetimeLocalInputValue(new Date());
}

export function defaultExpiryDate(daysFromNow = 1): Date {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date;
}

export function defaultExpiryDatetimeLocal(daysFromNow = 1): string {
  return toDatetimeLocalInputValue(defaultExpiryDate(daysFromNow));
}

if (__DEV__) {
  const sample = parseActiveSystemNotifications({
    a: {
      type: "warning",
      message: "x",
      createdAt: 200,
      expiresAt: 500,
      createdBy: "u",
    },
    b: {
      type: "information",
      message: "y",
      createdAt: 100,
      expiresAt: 500,
      createdBy: "u",
    },
  });
  if (!(sample.length === 2 && sample[0].id === "a" && sample[1].id === "b")) {
    console.error("systemNotifications: active sort by createdAt descending");
  }
}
